import { useState, MouseEvent, Dispatch, SetStateAction } from "react";
import { Button, Menu, MenuItem } from "@mui/material";
import { AlignVerticalTop } from "@mui/icons-material";
import { FilterSortGroup } from "../projectTypes";

interface CombinedOption {
  id: string;
  label: string;
  group: { id: string; label: string };
  groupSort: { id: string; label: string } | null;
}

interface ProjectDetailsDashWidgetGroupProps {
  filterSortGroup: FilterSortGroup;
  setFilterSortGroup: Dispatch<SetStateAction<FilterSortGroup>>;
}

const combinedOptions: CombinedOption[] = [
  {
    id: "phase-date",
    label: "Phase sorted by Start Date",
    group: { id: "group-phase", label: "Phase" },
    groupSort: { id: "sort-start-date", label: "Start Date" },
  },
  {
    id: "phase-name-asc",
    label: "Phase sorted by Name A-Z",
    group: { id: "group-phase", label: "Phase" },
    groupSort: { id: "sort-name-asc", label: "Name: A-Z" },
  },
  {
    id: "phase-name-desc",
    label: "Phase sorted by Name Z-A",
    group: { id: "group-phase", label: "Phase" },
    groupSort: { id: "sort-name-desc", label: "Name: Z-A" },
  },
  {
    id: "status",
    label: "Status",
    group: { id: "group-status", label: "Status" },
    groupSort: null,
  },
];

export default function ProjectDetailsDashWidgetGroup({
  filterSortGroup,
  setFilterSortGroup,
}: ProjectDetailsDashWidgetGroupProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const currentGroup = filterSortGroup?.grouping?.group ?? null;
  const currentGroupSort = filterSortGroup?.grouping?.groupSort ?? null;

  const handleOpen = (e: MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSelect = (opt: CombinedOption) => {
    setFilterSortGroup((prev: FilterSortGroup) => ({
      ...prev,
      grouping: {
        id: opt.id,
        group: opt.group,
        groupSort: opt.groupSort,
      },
    }));
    handleClose();
  };

  return (
    <>
      <Button
        color="inherit"
        variant="text"
        startIcon={<AlignVerticalTop />}
        onClick={handleOpen}
        sx={{ color: open ? "primary.main" : "text.secondary" }}
      >
        {currentGroup?.id
          ? `GROUP: ${currentGroup.label.toUpperCase()}${currentGroupSort ? ` (${currentGroupSort.label.toUpperCase()})` : ""}`
          : "GROUP"}
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {combinedOptions.map((opt) => {
          const selected: boolean = Boolean(
            currentGroup?.id === opt.group.id &&
              ((opt.groupSort === null && !currentGroupSort?.id) ||
                (opt.groupSort?.id &&
                  currentGroupSort?.id === opt.groupSort.id)),
          );
          return (
            <MenuItem
              key={opt.id}
              selected={selected}
              onClick={() => handleSelect(opt)}
            >
              {opt.label}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
